"use client";

import { cn } from "@workspace/ui/lib/utils";
import type { StoryStatus } from "./use-story-autoplay";
import { StoryPauseIcon, StoryPlayIcon } from "./story-icons";

export type StoryControlsProps = {
  count: number;
  index: number;
  runId: number;
  status: StoryStatus;
  /** After manual navigation the active pill shows no progress. */
  manual: boolean;
  running: boolean;
  /** Fill duration for the active slide. */
  durationMs: number;
  onSelect: (index: number) => void;
  onToggle: () => void;
  onProgressEnd: () => void;
};

const TOGGLE_LABEL: Record<StoryStatus, string> = {
  idle: "Play",
  paused: "Play",
  playing: "Pause",
};

// Mobile sits on the dark section, desktop on the light page (Figma 12620:20361 / 12668:88208).
// The bar is sticky and floats over the cards, so the translucent mobile fill blurs what's behind it.
const SURFACE = "bg-[rgba(51,51,51,0.5)] backdrop-blur-[12px] lg:bg-base-2";
// Expanded width of the active dot. The fill uses the same fixed width so its translateX(-100%)
// start never depends on the pill's width transition (compositors resolve % once, at animation start).
const ACTIVE_WIDTH = "w-10 lg:w-[53.33px]";

export function StoryControls({
  count,
  index,
  runId,
  status,
  manual,
  running,
  durationMs,
  onSelect,
  onToggle,
  onProgressEnd,
}: StoryControlsProps) {
  if (count <= 0) return null;

  return (
    <div
      id="tech-stories-controls"
      data-testid="tech-stories-controls"
      className="flex items-center justify-center gap-2.5 lg:gap-[18px]"
    >
      <div
        role="group"
        aria-label="Choose story"
        className={cn(
          "flex h-9 items-center gap-2 rounded-full px-3.5 lg:h-12 lg:gap-[10.67px] lg:px-[18.67px]",
          SURFACE,
        )}
      >
        {Array.from({ length: count }, (_, i) => {
          const isActive = i === index;

          return (
            <button
              key={i}
              type="button"
              id={`tech-stories-dot-${i + 1}`}
              data-testid={`tech-stories-dot-${i + 1}`}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={isActive ? "true" : undefined}
              onClick={() => onSelect(i)}
              // Horizontal padding (cancelled by negative margin) widens the tap target across the gap.
              className="-mx-1 flex h-full cursor-pointer items-center rounded-full px-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            >
              <span
                className={cn(
                  "relative block h-[9px] w-[9px] overflow-hidden rounded-full bg-light-grey/16 transition-[width] duration-500 ease-[cubic-bezier(0.65,0,0.35,1)] motion-reduce:transition-none lg:h-3 lg:w-3 lg:bg-base-4",
                  isActive && ACTIVE_WIDTH,
                )}
              >
                {/* After manual navigation the active pill stays an empty track: no progress. */}
                {isActive && !manual ? (
                  <span
                    key={`${index}-${runId}`}
                    aria-hidden="true"
                    className={cn(
                      "absolute inset-y-0 left-0 animate-[story-fill_linear_forwards] rounded-full bg-light-grey will-change-transform lg:bg-primary",
                      ACTIVE_WIDTH,
                    )}
                    style={{
                      animationDuration: `${durationMs}ms`,
                      animationPlayState: running ? "running" : "paused",
                    }}
                    onAnimationEnd={onProgressEnd}
                  />
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        id="tech-stories-toggle"
        data-testid="tech-stories-toggle"
        aria-label={TOGGLE_LABEL[status]}
        onClick={onToggle}
        className={cn(
          "flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-light-grey focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current lg:size-12 lg:text-primary",
          SURFACE,
        )}
      >
        {status === "playing" ? (
          <StoryPauseIcon className="h-[11.02px] w-[9.65px] lg:h-[14.7px] lg:w-[12.86px]" />
        ) : (
          <StoryPlayIcon className="ml-0.5 h-[11.02px] w-[9.34px] lg:h-[14.7px] lg:w-[12.46px]" />
        )}
      </button>
    </div>
  );
}
